import { useState, useEffect } from 'react';
import { getSites, getStores, createSite } from '../api/client';

interface Site {
  _id: string;
  name: string;
  category: string;
  coordinates?: {
    latitude: number;
    longitude: number;
    radius?: number;
  };
  isActive?: boolean;
}

interface Store {
  _id: string;
  siteId: string;
}

function AddSiteModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    category: 'Theme Park',
    latitude: '',
    longitude: '',
    radius: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.latitude || !formData.longitude) {
      setFormError('Name, latitude, and longitude are required');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      await createSite({
        name: formData.name.trim(),
        category: formData.category,
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
        radius: formData.radius ? Number(formData.radius) : undefined,
        description: formData.description.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create site');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">Add Site</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {formError && (
            <div className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm">{formError}</div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
            <input
              type="text"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g. Magic Kingdom"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category *</label>
            <select
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            >
              <option value="Theme Park">Theme Park</option>
              <option value="Resort">Resort</option>
              <option value="Shopping District">Shopping District</option>
              <option value="Water Park">Water Park</option>
            </select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Latitude *</label>
              <input
                type="number"
                step="any"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="28.4177"
                value={formData.latitude}
                onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Longitude *</label>
              <input
                type="number"
                step="any"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="-81.5812"
                value={formData.longitude}
                onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Radius (m)</label>
              <input
                type="number"
                step="any"
                min="0"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="1000"
                value={formData.radius}
                onChange={(e) => setFormData(prev => ({ ...prev, radius: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              rows={2}
              placeholder="Optional description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div className="flex gap-3 pt-2 border-t">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors">
              {saving ? 'Creating...' : 'Create Site'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Sites() {
  const [sites, setSites] = useState<Site[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddSite, setShowAddSite] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sitesResult, storesResult] = await Promise.all([
        getSites(),
        getStores(),
      ]);
      setSites(sitesResult.data?.sites as Site[] || []);
      setStores(storesResult.data?.stores as Store[] || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStoreCount = (siteId: string) => {
    return stores.filter(s => s.siteId === siteId).length;
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'theme park':
        return '&#x1F3A2;';
      case 'resort':
        return '&#x1F3E8;';
      case 'shopping district':
        return '&#x1F6CD;';
      case 'water park':
        return '&#x1F3CA;';
      default:
        return '&#x1F3F0;';
    }
  };

  const getCategoryBadgeClasses = (category: string) => {
    switch (category.toLowerCase()) {
      case 'theme park':
        return 'bg-purple-100 text-purple-700';
      case 'resort':
        return 'bg-blue-100 text-blue-700';
      case 'shopping district':
        return 'bg-pink-100 text-pink-700';
      case 'water park':
        return 'bg-cyan-100 text-cyan-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-start mb-8 pb-5 border-b-2 border-gray-200">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-1">Sites</h2>
          <div className="text-gray-500">{sites.length} Disney locations configured</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddSite(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
          >
            Add Site
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-12 text-gray-500">
          Loading sites...
        </div>
      ) : (
        <>
          {/* Sites Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {sites.map((site) => (
              <div key={site._id} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-slate-700 text-white p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-600 flex items-center justify-center text-xl" dangerouslySetInnerHTML={{ __html: getCategoryIcon(site.category) }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{site.name}</div>
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full ${getCategoryBadgeClasses(site.category)}`}>
                      {site.category}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Stores</div>
                      <div className="text-2xl font-bold text-slate-800">{getStoreCount(site._id)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Status</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className={`w-2.5 h-2.5 rounded-full ${site.isActive !== false ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <span className="text-sm text-slate-800">{site.isActive !== false ? 'Active' : 'Inactive'}</span>
                      </div>
                    </div>
                  </div>
                  {site.coordinates && (
                    <div className="text-xs text-gray-500 border-t border-gray-200 pt-3">
                      <span className="mr-1">&#x1F4CD;</span>
                      {site.coordinates.latitude.toFixed(4)}, {site.coordinates.longitude.toFixed(4)}
                      {site.coordinates.radius && ` (${site.coordinates.radius}m radius)`}
                    </div>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button className="flex-1 px-3 py-2 text-sm font-medium text-slate-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
                      Edit
                    </button>
                    <a
                      href={`/stores?site=${site._id}`}
                      className="flex-1 px-3 py-2 text-sm font-medium text-center text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors no-underline"
                    >
                      View Stores
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Category Summary */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-slate-700 text-white px-5 py-4">
              <span className="font-bold">&#x1F4CA; Sites by Category</span>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Theme Park', 'Resort', 'Shopping District', 'Water Park'].map((category) => {
                  const count = sites.filter(s => s.category.toLowerCase() === category.toLowerCase()).length;
                  const storeCount = sites
                    .filter(s => s.category.toLowerCase() === category.toLowerCase())
                    .reduce((acc, site) => acc + getStoreCount(site._id), 0);
                  return (
                    <div key={category} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span dangerouslySetInnerHTML={{ __html: getCategoryIcon(category) }} />
                        <span className="font-medium text-slate-800">{category}</span>
                      </div>
                      <div className="text-2xl font-bold text-slate-800">{count}</div>
                      <div className="text-xs text-gray-500">{storeCount} stores</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {showAddSite && (
        <AddSiteModal
          onClose={() => setShowAddSite(false)}
          onCreated={() => {
            setShowAddSite(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
}
